import assetUtils, { Asset } from 'app-lib/assetUtils'
import errorUtils from 'app-lib/errorUtils'
import providerUtils, { AnyProvider } from 'app-lib/providerUtils'
import swapRateUtils from 'app-lib/swapRateUtils'
import EarnRewardEventNew from 'bzx-common/src/domain/events/blockchain/EarnRewardEventNew'
import PayTradingFeeEvent from 'bzx-common/src/domain/events/blockchain/PayTradingFeeEvent'
import PositionEventsGroup from 'bzx-common/src/domain/fulcrum/PositionEventsGroup'
import PositionHistoryData from 'bzx-common/src/domain/fulcrum/PositionHistoryData'
import IBorrowedFundsState, { LoanReturnData } from 'bzx-common/src/domain/IBorrowedFundsState'
import { BigNumber } from '@0x/utils'
import { FulcrumProvider } from '../../../fulcrum/src/services/FulcrumProvider'
import appConfig, { chainConfigs, defaultLeverage } from '../config/appConfig'
import { ChainId, IChainConfig, IPairSettings } from '../config/chains/chainCommon'
import loansWithOldOpenPriceFormat from '../config/loansWithOldOpenPriceFormat'
import {
  CloseWithSwapEvent,
  DepositCollateralEvent,
  LiquidationEvent,
  RolloverEvent,
  TradeEvent,
  WithdrawCollateralEvent,
} from '../domain/events'
import { ITradeRequest, PositionType, TradeType, IOpenPositionProps } from '../domain/trade.types'
import oracleApi from './apis/oracleApi'
import collectionUtils from './collectionUtils'
import loanUtils from './loanUtils'
import subgraphHistoryUtils from './subgraphHistoryUtils'
import swapPayloadUtils from './swapPayloadUtils'
import { DEX } from './dexSelector/types'

function getDependenciesStrict(provider: AnyProvider) {
  const deps = providerUtils.getDependenciesStrict(provider)
  const bzxContract = deps.contractsSource.getiBZxContract()
  return { ...deps, bzxContract }
}

/**
 * Returns the wallet balance of the user of deposit token and from it, the maximum that can be
 * send to open the trade depending on the liquidity and gas buffer if it's the native token
 * The liquidity returned is in deposit token
 */
async function getMaxTradeValueToOpenPosition(
  provider: AnyProvider,
  params: {
    baseToken: Asset
    quoteToken: Asset
    depositToken: Asset
    positionType: PositionType
  }
): Promise<{
  maxTradeValue: BigNumber
  marketLiquidity: BigNumber
  depositTokenBalance: BigNumber
}> {
  const { baseToken, quoteToken, depositToken, positionType } = params
  const { contractsSource, helperContract, userAddress, chainId } =
    providerUtils.getDependenciesStrictRead(provider)
  let maxTradeValue = new BigNumber(0)
  let marketLiquidity = new BigNumber(0)

  const loanToken = positionType === PositionType.LONG ? quoteToken : baseToken
  const iToken = contractsSource.getITokenContract(loanToken)
  const precision = assetUtils.getDecimals(chainId, loanToken)

  marketLiquidity = (await helperContract.marketLiquidity([iToken.address]).callAsync())[0].div(
    10 ** precision
  )

  if (depositToken !== loanToken) {
    const swapPrice = await oracleApi.getSwapRate(provider, loanToken, depositToken)
    marketLiquidity = marketLiquidity.times(swapPrice)
  }

  const depositTokenBalance = userAddress
    ? await providerUtils.getTokenBalanceOfUser(provider, depositToken)
    : new BigNumber(0)

  maxTradeValue = BigNumber.min(marketLiquidity, depositTokenBalance)

  return {
    maxTradeValue,
    marketLiquidity,
    depositTokenBalance,
  }
}

async function getMaxTradeValueToClosePosition(
  provider: AnyProvider,
  params: {
    positionType: PositionType
    loan: IBorrowedFundsState
  }
) {
  // TODO: THIS IS NOT TAKING INTO ACCOUNT THE LIQUIDITY
  const { chainId } = providerUtils.getDependenciesStrictRead(provider)
  const result = { maxTradeValue: new BigNumber(0), marketLiquidity: new BigNumber(0) }
  const { loan, positionType } = params
  const loanAssetDecimals = assetUtils.getDecimals(chainId, loan.loanAsset)
  const collateralAssetDecimals = assetUtils.getDecimals(chainId, loan.collateralAsset)
  result.maxTradeValue =
    positionType === PositionType.LONG
      ? loan.loanData.collateral.div(10 ** collateralAssetDecimals)
      : loan.loanData.principal.div(10 ** loanAssetDecimals)
  return result
}

const getMaxTradeValue = async (
  provider: AnyProvider,
  params: {
    tradeType: TradeType
    baseToken: Asset
    quoteToken: Asset
    depositToken: Asset
    positionType: PositionType
    loan?: IBorrowedFundsState
  }
): Promise<{
  maxTradeValue: BigNumber
  marketLiquidity: BigNumber
  depositTokenBalance: BigNumber
}> => {
  if (params.tradeType === TradeType.OPEN_POSITION) {
    return getMaxTradeValueToOpenPosition(provider, params)
  }
  const { loan } = params
  if (!loan) {
    throw new Error('Missing loan in getMaxTradeValue')
  }
  const maxTradeValue = await getMaxTradeValueToClosePosition(provider, { ...params, loan })
  return { ...maxTradeValue, depositTokenBalance: new BigNumber(0) }
}

async function getEstimatedMarginDetails(
  provider: AnyProvider,
  request: ITradeRequest,
  opts: { loanDataBytes: string }
): Promise<{
  principal: BigNumber
  collateral: BigNumber
  exposureValue: BigNumber
  interestRate: BigNumber
  collateralToLoanRate: BigNumber
}> {
  const isLong = request.positionType === PositionType.LONG
  const { contractsSource, chainId } = providerUtils.getDependenciesStrict(provider)

  const collateralToken = isLong ? request.baseToken : request.quoteToken
  const loanToken = isLong ? request.quoteToken : request.baseToken
  const leverage = isLong ? request.leverage - 1 : request.leverage
  const itokenContract = contractsSource.getITokenContract(loanToken)
  const depositToken = request.depositToken
  const decimals = assetUtils.getDecimals(chainId, depositToken)
  const collateralTokenDecimals = assetUtils.getDecimals(chainId, collateralToken)
  const loanTokenDecimals = assetUtils.getDecimals(chainId, loanToken)
  const amountInBaseUnits = new BigNumber(request.amount.times(10 ** decimals).toFixed(0, 1))
  const collateralTokenSent =
    depositToken === collateralToken ? amountInBaseUnits : new BigNumber(0)
  const loanTokenSent = depositToken === loanToken ? amountInBaseUnits : new BigNumber(0)

  const collateralToLoanRate = await oracleApi.getSwapRateNew(provider, collateralToken, loanToken)
  const collateralInLoanToken = collateralToLoanRate.times(collateralTokenSent)
  const principal = loanTokenSent.plus(collateralInLoanToken).times(leverage)

  const interestRate = await itokenContract.nextBorrowInterestRate(principal).callAsync()

  const swappedAmount = await swapRateUtils.getSwapExpectedReturn(provider, {
    srcToken: loanToken,
    destToken: collateralToken,
    srcAmount: principal.plus(loanTokenSent),
    loanDataBytes: opts.loanDataBytes,
  })

  const collateral = swappedAmount.plus(collateralTokenSent.div(10 ** collateralTokenDecimals))
  const exposureValue = isLong ? collateral : principal.div(10 ** loanTokenDecimals)

  return {
    principal: principal.div(10 ** loanTokenDecimals),
    collateral,
    exposureValue, // some amount somewhere close position or something
    interestRate: interestRate.div(10 ** 18), // how much user is paying
    collateralToLoanRate,
  }
}

/**
 * Get all open positions (loans) of the user
 */
async function getUserMarginTradeLoans(provider: AnyProvider): Promise<IBorrowedFundsState[]> {
  const { bzxContract, userAddress, chainId } = getDependenciesStrict(provider)
  const result: IBorrowedFundsState[] = []
  const loansData = await bzxContract
    .getUserLoans(
      userAddress,
      new BigNumber(0),
      new BigNumber(50),
      1, // margin trade loans
      false,
      false
    )
    .callAsync()

  loansData.forEach((e: LoanReturnData) => {
    const loanAsset = assetUtils.getAssetFromAddress(chainId, e.loanToken)
    const collateralAsset = assetUtils.getAssetFromAddress(chainId, e.collateralToken)
    if (loanAsset === Asset.UNKNOWN || collateralAsset === Asset.UNKNOWN) {
      return
    }
    const loanPrecision = assetUtils.getDecimals(chainId, loanAsset)
    const collateralPrecision = assetUtils.getDecimals(chainId, collateralAsset)
    let amountOwned = e.principal.minus(e.interestDepositRemaining)
    if (amountOwned.lte(0)) {
      amountOwned = new BigNumber(0)
    } else {
      amountOwned = amountOwned.dividedBy(10 ** loanPrecision).dp(5, BigNumber.ROUND_CEIL)
    }
    result.push({
      accountAddress: userAddress,
      loanId: e.loanId,
      loanAsset: loanAsset,
      collateralAsset: collateralAsset,
      amount: e.principal.dividedBy(10 ** loanPrecision).dp(5, BigNumber.ROUND_CEIL),
      amountOwed: amountOwned,
      collateralAmount: e.collateral.dividedBy(10 ** collateralPrecision),
      collateralizedPercent: e.currentMargin.dividedBy(10 ** 20),
      interestRate: e.interestOwedPerDay.dividedBy(e.principal).multipliedBy(365),
      interestOwedPerDay: e.interestOwedPerDay.dividedBy(10 ** loanPrecision),
      hasManagementContract: true,
      isInProgress: false,
      loanData: e,
    } as IBorrowedFundsState)
  })

  return result
}

async function getLoanCloseAmount(
  provider: AnyProvider,
  request: ITradeRequest,
  loan: IBorrowedFundsState
): Promise<[BigNumber, BigNumber, string | Error]> {
  const { userAddress, bzxContract, chainId } = getDependenciesStrict(provider)
  let result: [BigNumber, BigNumber, string | Error] = [new BigNumber(0), new BigNumber(0), '']
  let amountInBaseUnits = new BigNumber(0)
  const returnToken = request.returnTokenIsCollateral ? loan.collateralAsset : loan.loanAsset

  if (request.positionType === PositionType.LONG) {
    amountInBaseUnits = providerUtils.inBaseUnits(chainId, loan.collateralAsset, request.amount)
  } else {
    const { maxTradeValue } = await getMaxTradeValue(provider, {
      tradeType: TradeType.CLOSE_POSITION,
      baseToken: loan.loanAsset,
      quoteToken: loan.collateralAsset,
      depositToken: returnToken,
      positionType: request.positionType,
      loan,
    })
    amountInBaseUnits = new BigNumber(
      loan.loanData.collateral.times(request.amount.div(maxTradeValue)).toFixed(0, 1)
    )
  }

  const maxAmountInBaseUnits = loan.loanData.collateral

  if (
    maxAmountInBaseUnits.gt(0) &&
    maxAmountInBaseUnits.minus(amountInBaseUnits).abs().div(maxAmountInBaseUnits).lte(0.01)
  ) {
    amountInBaseUnits = new BigNumber(maxAmountInBaseUnits.times(10 ** 50).toFixed(0, 1))
  }

  try {
    result = await bzxContract
      .closeWithSwap(
        loan.loanId,
        userAddress,
        amountInBaseUnits,
        request.returnTokenIsCollateral,
        request.loanDataBytes
      )
      .callAsync({
        from: userAddress,
        gas: appConfig.gas.gasLimitFallback,
      })
  } catch (err) {
    result[2] = new Error('Could not get LoanCloseAmount. ' + errorUtils.getErrorStackMessages(err)) //send error message
    console.error(result[2])
    return result
  }
  const decimals = assetUtils.getDecimals(chainId, returnToken)

  result[1] = result[1].div(10 ** decimals)

  return result
}

const getManageCollateralExcessAmount = async (
  provider: AnyProvider,
  borrowedFundsState: IBorrowedFundsState
): Promise<BigNumber> => {
  const { contractsSource, userAddress, chainId } = providerUtils.getDependenciesStrict(provider)
  const { loanId, collateralAsset } = borrowedFundsState
  const bZxContract = contractsSource.getiBZxContract()
  const precision = assetUtils.getDecimals(chainId, collateralAsset)
  const tx = bZxContract.withdrawCollateral(loanId, userAddress, appConfig.MAX_UINT)
  const result = await tx.callAsync({ from: userAddress, gas: appConfig.gas.gasLimitFallback })
  return result.dividedBy(10 ** precision)
}

/**
 * Try to guess if it was a long or a short based on our list of tokens and the loan data
 * Reminder: from the protocol point of view, there are no long / short, just loans.
 */
function guessLongShort(chainId: ChainId, loanAsset: Asset, collateralAsset: Asset) {
  let positionType
  const { base: baseTokens, quote: quoteTokens } = chainConfigs[chainId].trading.baseQuote
  const tradePairs = chainConfigs[chainId].trading.pairs

  const possiblePairs = tradePairs.filter(
    (pair: Asset[]) =>
      (pair[0] === loanAsset && pair[1] === collateralAsset) ||
      (pair[0] === collateralAsset && pair[1] === loanAsset)
  )
  if (possiblePairs && possiblePairs.length > 0) {
    if (possiblePairs.length > 1) {
      console.error(
        "The position fits to more than one pair. Couldn't treat it exactly as LONG/SHORT"
      )
    }
    positionType = possiblePairs[0][0] === collateralAsset ? PositionType.LONG : PositionType.SHORT
  } else {
    const isLoanTokenOnlyInQuoteTokens =
      !baseTokens.includes(loanAsset) && quoteTokens.includes(loanAsset)
    const isCollateralTokenNotInQuoteTokens =
      baseTokens.includes(collateralAsset) && !quoteTokens.includes(collateralAsset)
    positionType =
      isCollateralTokenNotInQuoteTokens || isLoanTokenOnlyInQuoteTokens
        ? PositionType.LONG
        : PositionType.SHORT
  }
  const isLong = positionType === PositionType.LONG
  const baseAsset = isLong ? collateralAsset : loanAsset
  const quoteAsset = isLong ? loanAsset : collateralAsset

  return { baseAsset, quoteAsset, positionType, isLong }
}

/**
 * Get the open price from a loan while handling legacy loans format
 */
function getOpenPrice(chainId: ChainId, loan: IBorrowedFundsState): BigNumber {
  const { isLong } = guessLongShort(chainId, loan.loanAsset, loan.collateralAsset)
  const loanAssetDecimals = assetUtils.getDecimals(chainId, loan.loanAsset)
  const collateralAssetDecimals = assetUtils.getDecimals(chainId, loan.collateralAsset)
  const loanAssetPrecision = new BigNumber(10 ** (18 - loanAssetDecimals))
  const collateralAssetPrecision = new BigNumber(10 ** (18 - collateralAssetDecimals))

  let openPrice = new BigNumber(NaN)
  if (isLong) {
    // earlier startRate was stored from Chainlink and had this format
    openPrice = loan.loanData.startRate
      .div(10 ** 18)
      .times(loanAssetPrecision)
      .div(collateralAssetPrecision)
    // https://github.com/OokiTrade/contractsV2/commit/2afdeb8c6b9951456d835fbd90a6bc38c699de89
    // but then we started to store startRate as a price from Kyber (the real swap rate from trade event)
    // that has another format and should be handled in the following way:
    const newOpenPrice = new BigNumber(10 ** 18)
      .div(loan.loanData.startRate)
      .times(10 ** (collateralAssetDecimals - loanAssetDecimals))

    // the wrong price will be much larger.
    // For example 307854115598597579198986971899 and 0.03263155. The latest is the correct price
    openPrice = !loansWithOldOpenPriceFormat.includes(loan.loanId) ? newOpenPrice : openPrice
  } else {
    // earlier startRate was stored from Chainlink and had this format
    openPrice = new BigNumber(10 ** 18).div(
      loan.loanData.startRate.times(loanAssetPrecision).div(collateralAssetPrecision)
    )

    // https://github.com/OokiTrade/contractsV2/commit/2afdeb8c6b9951456d835fbd90a6bc38c699de89
    // but then we started to store startRate as a price from Kyber (the real swap rate from trade event)
    // that has another format and should be handled in the following way:
    const newOpenPrice = loan.loanData.startRate
      .div(10 ** 18)
      .times(10 ** (loanAssetDecimals - collateralAssetDecimals))

    // the wrong price will be much larger.
    // For example 307854115598597579198986971899 and 0.03263155. The latest is the correct price
    openPrice = !loansWithOldOpenPriceFormat.includes(loan.loanId) ? newOpenPrice : openPrice
  }
  return openPrice
}

/** For a loan, returns the oracle price of collateral / principal */
async function getLoanMarketRate(
  provider: AnyProvider,
  loan: IBorrowedFundsState,
  isLong: boolean
) {
  const currentCollateralToPrincipalRate = await oracleApi.getSwapRate(
    provider,
    loan.collateralAsset,
    loan.loanAsset
  )

  return isLong ? currentCollateralToPrincipalRate : currentCollateralToPrincipalRate.pow(-1)
}

// TODO: REFACTOR: THIS IS CALLING TOO MANY THINGS, they should be split and handled separately.
async function getLoanDetails(
  provider: AnyProvider,
  loan: IBorrowedFundsState
): Promise<Partial<IOpenPositionProps>> {
  // approx date when Open Price precision update was deployed
  // https://github.com/OokiTrade/contractsV2/commit/2afdeb8c6b9951456d835fbd90a6bc38c699de89
  // https://etherscan.io/tx/0xd69e0d550a665975ce963b4069206257c279223bf3fda4cbe019efff2b70bf61
  const { currentChainId: chainId } = provider
  // maintenanceMargin = margin * 100% * (10 ** 18)
  const maintenanceMargin = loan.loanData.maintenanceMargin.div(10 ** 20)

  const { positionType, baseAsset, quoteAsset, isLong } = guessLongShort(
    chainId,
    loan.loanAsset,
    loan.collateralAsset
  )

  const currentCollateralToPrincipalRate = await getLoanMarketRate(provider, loan, isLong)

  const currentPrice = isLong
    ? currentCollateralToPrincipalRate
    : currentCollateralToPrincipalRate.pow(-1)

  // startMargin = value * 100% * (10 ** 18)
  let leverage = new BigNumber(10 ** 20)
    .div(loan.loanData.startMargin)
    .dp(0, BigNumber.ROUND_HALF_UP)
  if (isLong) {
    leverage = leverage.plus(1)
  }

  let positionValue = new BigNumber(0)
  let value = new BigNumber(0)
  let collateral = new BigNumber(0)
  let profitCollateralToken = new BigNumber(NaN)
  let profitLoanToken = new BigNumber(NaN)
  let profitUSD = new BigNumber(0)

  const loanAssetDecimals = assetUtils.getDecimals(chainId, loan.loanAsset)
  const collateralAssetDecimals = assetUtils.getDecimals(chainId, loan.collateralAsset)
  const collateralAssetAmount = loan.loanData.collateral.div(10 ** collateralAssetDecimals)
  const loanAssetAmount = loan.loanData.principal.div(10 ** loanAssetDecimals)
  const depositValueAsLoanToken = loan.loanData.depositValueAsLoanToken.div(10 ** loanAssetDecimals)
  const depositValueAsCollateralToken = loan.loanData.depositValueAsCollateralToken.div(
    10 ** collateralAssetDecimals
  )

  // liquidationPrice = ((maintenance_margin * principal / 10 ** 20) + principal) / collateral
  const liquidationPrice = maintenanceMargin
    .times(loanAssetAmount)
    .plus(loanAssetAmount)
    .div(collateralAssetAmount)

  const { maxTradeValue } = await getMaxTradeValue(provider, {
    tradeType: TradeType.CLOSE_POSITION,
    baseToken: loan.loanAsset,
    quoteToken: loan.collateralAsset,
    depositToken: Asset.UNKNOWN,
    positionType,
    loan,
  })

  /** We use the first dex available for the chain to get the current profit */
  const dex = chainConfigs[chainId].dexes[0]

  const tradeRequestCollateral: ITradeRequest = {
    amount: maxTradeValue,
    baseToken: baseAsset,
    // Using wrapped to avoids a problem with getting deposit token info until open and close requests are split
    // Because "closePosition" does not have a depositToken but it's forced on ITradeRequest interface
    depositToken: chainConfigs[chainId].native.wrapped,
    leverage: leverage.toNumber(),
    loanDataBytes: '0x',
    loanId: loan.loanId,
    positionType,
    quoteToken: quoteAsset,
    returnTokenIsCollateral: true,
    tradeType: TradeType.CLOSE_POSITION,
    dex,
  }

  const tradeRequestLoan: ITradeRequest = {
    ...tradeRequestCollateral,
    returnTokenIsCollateral: false,
  }

  const openPrice = getOpenPrice(chainId, loan)

  if (dex === DEX.UNISWAPV3) {
    tradeRequestCollateral.loanDataBytes = (
      await swapPayloadUtils.getSwapDataForClosingPosition(provider, tradeRequestCollateral, loan)
    )[0]
    tradeRequestLoan.loanDataBytes = (
      await swapPayloadUtils.getSwapDataForClosingPosition(provider, tradeRequestLoan, loan)
    )[0]
  }

  if (isLong) {
    positionValue = collateralAssetAmount
    value = collateralAssetAmount.times(currentCollateralToPrincipalRate)
    collateral = loanAssetAmount

    if (depositValueAsCollateralToken.eq(0) || depositValueAsLoanToken.eq(0)) {
      profitUSD = currentCollateralToPrincipalRate.minus(openPrice).times(positionValue)
    } else {
      const estimatedCollateralReceived = await getLoanCloseAmount(
        provider,
        tradeRequestCollateral,
        loan
      )
      const estimatedLoanReceived = await getLoanCloseAmount(provider, tradeRequestLoan, loan)
      const estimatedReceivedCollateralToken = estimatedCollateralReceived[1]
      const estimatedReceivedLoanToken = estimatedLoanReceived[1]
      profitCollateralToken = estimatedReceivedCollateralToken.gt(0)
        ? estimatedReceivedCollateralToken.minus(depositValueAsCollateralToken)
        : new BigNumber(NaN)
      profitLoanToken = estimatedReceivedLoanToken.gt(0)
        ? estimatedReceivedLoanToken.minus(depositValueAsLoanToken)
        : new BigNumber(NaN)
    }
  } else {
    collateral = collateralAssetAmount.times(currentCollateralToPrincipalRate)

    const shortsDiff = collateralAssetAmount
      .times(currentCollateralToPrincipalRate)
      .minus(loanAssetAmount)

    positionValue = collateralAssetAmount.times(currentCollateralToPrincipalRate).minus(shortsDiff)
    value = positionValue.div(currentCollateralToPrincipalRate)

    if (depositValueAsCollateralToken.eq(0) || depositValueAsLoanToken.eq(0)) {
      profitUSD = openPrice
        .minus(new BigNumber(1).div(currentCollateralToPrincipalRate))
        .times(positionValue)
    } else {
      const estimatedReceivedCollateralToken = (
        await getLoanCloseAmount(provider, tradeRequestCollateral, loan)
      )[1]
      const estimatedReceivedLoanToken = (
        await getLoanCloseAmount(provider, tradeRequestLoan, loan)
      )[1]
      profitCollateralToken = estimatedReceivedCollateralToken.gt(0)
        ? estimatedReceivedCollateralToken.minus(depositValueAsCollateralToken)
        : new BigNumber(NaN)
      profitLoanToken = estimatedReceivedLoanToken.gt(0)
        ? estimatedReceivedLoanToken.minus(depositValueAsLoanToken)
        : new BigNumber(NaN)
    }
  }

  return {
    chainId,
    loan,
    baseToken: baseAsset,
    quoteToken: quoteAsset,
    leverage: leverage.toNumber(),
    positionType,
    positionValue,
    value,
    collateral: depositValueAsCollateralToken.gt(0) ? depositValueAsCollateralToken : collateral,
    openPrice,
    currentPrice,
    liquidationPrice,
    profitCollateralToken,
    profitLoanToken,
    profitUSD,
    maintenanceMargin,
  } as Partial<IOpenPositionProps>
}

const getHistoryEvents = async (
  provider: FulcrumProvider,
  opts?: { oldGraph: boolean; allNetworks: boolean }
) => {
  if (
    !provider.web3Wrapper ||
    !provider.contractsSource ||
    !provider.currentChainId ||
    !provider.contractsSource.canWrite
  ) {
    return null
  }

  const account = provider.currentAccount
  const historyEvents = await subgraphHistoryUtils.getTradeHistory(
    provider.currentChainId,
    account,
    opts
  )
  const payTradingFeeEvents: PayTradingFeeEvent[] = []
  const earnRewardEvents: EarnRewardEventNew[] = []
  const events = historyEvents.reduce((result, e) => {
    if (e instanceof PayTradingFeeEvent) {
      payTradingFeeEvents.push(e)
      return result
    }
    if (e instanceof EarnRewardEventNew) {
      earnRewardEvents.push(e)
      return result
    }
    result.push(e)
    return result
  }, [] as Array<TradeEvent | LiquidationEvent | CloseWithSwapEvent | DepositCollateralEvent | WithdrawCollateralEvent | RolloverEvent>)

  // Group array of events by loanId
  const groupedEvents = collectionUtils.groupBy<
    | TradeEvent
    | LiquidationEvent
    | CloseWithSwapEvent
    | DepositCollateralEvent
    | WithdrawCollateralEvent
    | RolloverEvent
  >(
    events.sort((a, b) => b.blockNumber.minus(a.blockNumber).toNumber()),
    ['loanId']
  )
  for (const loanId of Object.keys(groupedEvents)) {
    const eventsById = groupedEvents[loanId]
    const isContainTradeEvent = eventsById.some(
      (
        e:
          | TradeEvent
          | LiquidationEvent
          | RolloverEvent
          | CloseWithSwapEvent
          | DepositCollateralEvent
          | WithdrawCollateralEvent
      ) => e instanceof TradeEvent
    )
    if (!isContainTradeEvent) {
      delete groupedEvents[loanId]
    }
  }

  return { groupedEvents, earnRewardEvents, payTradingFeeEvents }
}

interface IHistoryEvents {
  groupedEvents: {
    [key: string]: (
      | TradeEvent
      | LiquidationEvent
      | RolloverEvent
      | CloseWithSwapEvent
      | DepositCollateralEvent
      | WithdrawCollateralEvent
    )[]
  }
  earnRewardEvents: EarnRewardEventNew[]
  payTradingFeeEvents: PayTradingFeeEvent[]
}

export async function getHistoryRowsData(provider: AnyProvider, historyEvents: IHistoryEvents) {
  const chainId = provider.currentChainId
  // https://etherscan.io/tx/0x4c4e621687b18ffd9a7884f58ed8e656cec0fc06e6a6563b63fe5f0c46e0292a
  const blockNumberWhenPricePrecisionWasChanged = 11270966
  const historyRowsData = []
  const loanIds = Object.keys(historyEvents.groupedEvents)

  for (const loanId of loanIds) {
    // @ts-ignore
    const events = historyEvents.groupedEvents[loanId].sort((a: any, b: any) =>
      a.blockNumber.minus(b.blockNumber).toNumber()
    )
    const tradeEvent = events[0] as TradeEvent

    const { positionType, baseAsset, quoteAsset, isLong } = guessLongShort(
      chainId,
      tradeEvent.loanToken,
      tradeEvent.collateralToken
    )

    if (!tradeEvent.entryLeverage) {
      continue
    }
    let leverage = new BigNumber(tradeEvent.entryLeverage.div(10 ** 18))

    if (isLong) {
      leverage = leverage.plus(1)
    }

    const loanAssetDecimalsFirstEvent = assetUtils.getDecimals(chainId, tradeEvent.loanToken)
    const collateralAssetDecimalsFirstEvent = assetUtils.getDecimals(
      chainId,
      tradeEvent.collateralToken
    )

    const isLegacyPrecision =
      chainId === 1 && tradeEvent.blockNumber.lte(blockNumberWhenPricePrecisionWasChanged)

    const openPrice = isLong
      ? !isLegacyPrecision
        ? new BigNumber(10 ** 36)
            .div(tradeEvent.entryPrice)
            .div(10 ** 18)
            .times(10 ** (collateralAssetDecimalsFirstEvent - loanAssetDecimalsFirstEvent))
        : new BigNumber(10 ** 36).div(tradeEvent.entryPrice).div(10 ** 18)
      : !isLegacyPrecision
      ? tradeEvent.entryPrice
          .div(10 ** 18)
          .times(10 ** (loanAssetDecimalsFirstEvent - collateralAssetDecimalsFirstEvent))
      : tradeEvent.entryPrice.div(10 ** 18)

    const positionEventsGroup = new PositionEventsGroup(
      loanId,
      baseAsset,
      quoteAsset,
      positionType,
      leverage.toNumber()
    )
    for (const event of events) {
      let positionValue = new BigNumber(0)
      let tradePrice = new BigNumber(0)
      let value = new BigNumber(0)
      let profit: BigNumber | string = '-'
      let timeStamp: Date
      try {
        timeStamp =
          // @ts-ignore
          event.timestamp ||
          new Date(
            (await provider.web3Wrapper!.getBlockTimestampAsync(event.blockNumber.toNumber())) *
              1000
          )
      } catch (e) {
        console.error(e)
        timeStamp = new Date()
        // continue
      }
      const txHash = event.txHash
      const payTradingFeeEvent = historyEvents.payTradingFeeEvents.find((e) =>
        e.blockNumber.eq(event.blockNumber)
      )

      const earnRewardEvent = historyEvents.earnRewardEvents.find((e) =>
        e.blockNumber.eq(event.blockNumber)
      )
      if (event instanceof TradeEvent) {
        const action = 'Opened'
        const loanAssetDecimals = assetUtils.getDecimals(chainId, event.loanToken)
        const collateralAssetDecimals = assetUtils.getDecimals(chainId, event.collateralToken)

        if (isLong) {
          positionValue = event.positionSize.div(10 ** collateralAssetDecimals)
          tradePrice = !isLegacyPrecision
            ? new BigNumber(10 ** 36)
                .div(event.entryPrice)
                .div(10 ** 18)
                .times(10 ** (collateralAssetDecimals - loanAssetDecimals))
            : new BigNumber(10 ** 36).div(event.entryPrice).div(10 ** 18)
          value = positionValue.times(tradePrice)
        } else {
          positionValue = event.borrowedAmount.div(10 ** loanAssetDecimals)
          tradePrice = !isLegacyPrecision
            ? event.entryPrice
                .div(10 ** 18)
                .times(10 ** (loanAssetDecimals - collateralAssetDecimals))
            : event.entryPrice.div(10 ** 18)
          value = positionValue.times(tradePrice)
        }

        positionEventsGroup.events.push(
          new PositionHistoryData(
            loanId,
            timeStamp,
            action,
            positionValue,
            tradePrice,
            value,
            profit,
            txHash,
            quoteAsset,
            payTradingFeeEvent,
            earnRewardEvent
          )
        )
      } else if (event instanceof CloseWithSwapEvent) {
        const action = 'Closed'
        const loanAssetDecimals = assetUtils.getDecimals(chainId, event.loanToken)
        const collateralAssetDecimals = assetUtils.getDecimals(chainId, event.collateralToken)

        if (isLong) {
          positionValue = event.positionCloseSize.div(10 ** collateralAssetDecimals)
          tradePrice = !isLegacyPrecision
            ? new BigNumber(10 ** 36)
                .div(event.exitPrice)
                .div(10 ** 18)
                .times(10 ** (collateralAssetDecimals - loanAssetDecimals))
            : new BigNumber(10 ** 36).div(event.exitPrice).div(10 ** 18)
          value = positionValue.times(tradePrice)
          profit = tradePrice.minus(openPrice).times(positionValue)
        } else {
          positionValue = event.loanCloseAmount.div(10 ** loanAssetDecimals)
          tradePrice = !isLegacyPrecision
            ? event.exitPrice
                .div(10 ** 18)
                .times(10 ** (loanAssetDecimals - collateralAssetDecimals))
            : event.exitPrice.div(10 ** 18)
          value = positionValue.times(tradePrice)
          profit = openPrice.minus(tradePrice).times(positionValue)
        }

        positionEventsGroup.events.push(
          new PositionHistoryData(
            loanId,
            timeStamp,
            action,
            positionValue,
            tradePrice,
            value,
            profit,
            txHash,
            quoteAsset,
            payTradingFeeEvent,
            earnRewardEvent
          )
        )
      } else if (event instanceof LiquidationEvent) {
        // loanToken in LiquidationEvent is a quoteAsset in TradeEvent
        // collateralToken in LiquidationEvent is a baseAsset in TradeEvent
        const action = 'Liquidated'
        const loanAssetDecimals = assetUtils.getDecimals(chainId, event.asset)
        const collateralAssetDecimals = assetUtils.getDecimals(chainId, event.collateralToken)

        if (isLong) {
          tradePrice = event.collateralToLoanRate
            .div(10 ** 18)
            .times(10 ** (collateralAssetDecimals - loanAssetDecimals))
          positionValue = event.repayAmount.div(10 ** loanAssetDecimals).div(tradePrice)
          value = positionValue.times(tradePrice)
          profit = value.minus(
            event.collateralWithdrawAmount.div(10 ** collateralAssetDecimals).times(tradePrice)
          )
        } else {
          positionValue = event.repayAmount.div(10 ** loanAssetDecimals)
          tradePrice = new BigNumber(10 ** 36)
            .div(event.collateralToLoanRate)
            .div(10 ** 18)
            .times(10 ** (loanAssetDecimals - collateralAssetDecimals))
          value = positionValue.times(tradePrice)
          profit = value.minus(event.collateralWithdrawAmount.div(10 ** collateralAssetDecimals))
        }

        positionEventsGroup.events.push(
          new PositionHistoryData(
            loanId,
            timeStamp,
            action,
            positionValue,
            tradePrice,
            value,
            profit,
            txHash,
            quoteAsset,
            payTradingFeeEvent,
            earnRewardEvent
          )
        )
      } else if (event instanceof DepositCollateralEvent) {
        const action = 'Deposited'
        const depositTokenDecimals = assetUtils.getDecimals(chainId, event.asset)

        if (isLong) {
          positionValue = event.amount.div(10 ** depositTokenDecimals)
          tradePrice = new BigNumber(0)
        } else {
          tradePrice = new BigNumber(0)
          positionValue = event.amount.div(10 ** depositTokenDecimals).times(tradePrice)
        }

        positionEventsGroup.events.push(
          new PositionHistoryData(
            loanId,
            timeStamp,
            action,
            positionValue,
            tradePrice,
            value,
            profit,
            txHash,
            quoteAsset,
            payTradingFeeEvent,
            earnRewardEvent
          )
        )
      } else if (event instanceof WithdrawCollateralEvent) {
        const action = 'Withdrew'
        const withdrawTokenDecimals = assetUtils.getDecimals(chainId, event.asset)

        if (isLong) {
          positionValue = event.amount.div(10 ** withdrawTokenDecimals)
          tradePrice = new BigNumber(0)
        } else {
          tradePrice = new BigNumber(0)
          positionValue = event.amount.div(10 ** withdrawTokenDecimals).times(tradePrice)
        }

        positionEventsGroup.events.push(
          new PositionHistoryData(
            loanId,
            timeStamp,
            action,
            positionValue,
            tradePrice,
            value,
            profit,
            txHash,
            quoteAsset,
            payTradingFeeEvent,
            earnRewardEvent
          )
        )
      } else if (event instanceof RolloverEvent) {
        const action = 'Rollovered'

        tradePrice = new BigNumber(0)

        positionEventsGroup.events.push(
          new PositionHistoryData(
            loanId,
            timeStamp,
            action,
            positionValue,
            tradePrice,
            value,
            profit,
            txHash,
            quoteAsset,
            payTradingFeeEvent,
            earnRewardEvent
          )
        )
      } else {
        // do nothing
      }
    }
    historyRowsData.push({
      eventsGroup: positionEventsGroup,
      isHidden: false,
    })
  }
  return historyRowsData
}

/**
 * Helper for repetitive trade request assets data gathering
 * Determines loaned and collateral assets, leverage param to be passed based on long / short
 * src and dest are used to calculate swaps
 */
function getTradeTokens(
  chainId: ChainId,
  params: {
    baseToken: Asset
    depositToken: Asset
    positionType: PositionType
    quoteToken: Asset
    tradeType: TradeType
    leverage: number
  }
) {
  const isLong = params.positionType === PositionType.LONG
  const isClosingTrade = params.tradeType === TradeType.CLOSE_POSITION
  const leverage = isLong ? params.leverage - 1 : params.leverage
  const [base, quote, deposit] = assetUtils.getTokensInfo(chainId, [
    params.baseToken,
    params.quoteToken,
    params.depositToken,
  ])

  const src = (isLong && !isClosingTrade) || (!isLong && isClosingTrade) ? quote : base
  const dest = src.asset === params.baseToken ? quote : base
  const loaned = isLong ? quote : base
  const collateral = isLong ? base : quote

  return { src, dest, loaned, collateral, deposit, leverage }
}

async function getLiquidationPrice(
  provider: AnyProvider,
  request: ITradeRequest,
  collateralToPrincipalRate: BigNumber,
  loanParams?: { maintenanceMargin: BigNumber }
) {
  const { chainId } = providerUtils.getDependenciesStrictRead(provider)
  const { src, dest, leverage } = getTradeTokens(chainId, request)
  const initialMargin = new BigNumber(100).div(leverage)

  const maintenanceMargin =
    loanParams?.maintenanceMargin ||
    (await loanUtils.getLoanParams(provider, src.asset, dest.asset, false)).maintenanceMargin

  // liq_price_before_trade = (maintenance_margin / 10^20 * collateralToLoanRate) + collateralToLoanRate) / ((100 + current_margin) / 100
  // if it's a SHORT then -> 1/ above

  const liquidationPriceBeforeTrade = maintenanceMargin
    .div(10 ** 20)
    .times(collateralToPrincipalRate)
    .plus(collateralToPrincipalRate)
    .div(new BigNumber(100).plus(initialMargin).div(100))

  return request.positionType === PositionType.LONG
    ? liquidationPriceBeforeTrade
    : liquidationPriceBeforeTrade.pow(-1)
}

/** See tradeUtils.getPairConfig */
export interface IPairConfigDetails {
  chain: IChainConfig
  depositTokens: Asset[]
  dexes: DEX[]
  initialPositionType: PositionType
  isStablePair: boolean
  leverageInitial: number
  leverageMax: number
  pairSettings?: IPairSettings
  selectedMarket: { baseToken: Asset; quoteToken: Asset }
}

/**
 * Helper to know whether a user can long/short, which token he can deposit, etc for a pair
 * See chainCommon for how pair specific configs can be created
 */
export function getPairConfig(
  pair: { baseToken: Asset; quoteToken: Asset },
  chainId: ChainId
): IPairConfigDetails {
  const chain = chainConfigs[chainId]
  const { baseToken, quoteToken } = pair
  const baseConfig = chainConfigs[chainId].trading.pairConfigs[baseToken]
  const pairSettings = baseConfig ? baseConfig[quoteToken] : undefined
  const isStablePair = assetUtils.isStablePair(pair)
  const dexes = pairSettings?.dexes || chain.dexes
  const initialPositionType = pairSettings?.disabledPosition.LONG
    ? PositionType.SHORT
    : PositionType.LONG
  const depositTokens = (pairSettings?.depositTokens &&
    pairSettings?.depositTokens[initialPositionType]) || [baseToken, quoteToken]
  const leverageMax =
    pairSettings?.maxLeverage ||
    (isStablePair ? appConfig.maxLeverage.stablePair : appConfig.maxLeverage.others)

  const leverageInitial = isStablePair ? defaultLeverage.stablePair : defaultLeverage.others

  return {
    chain,
    depositTokens,
    dexes,
    initialPositionType,
    isStablePair,
    leverageInitial,
    leverageMax,
    pairSettings,
    selectedMarket: pair,
  }
}

export default {
  getLoanMarketRate,
  getEstimatedMarginDetails,
  getHistoryEvents,
  getHistoryRowsData,
  getLiquidationPrice,
  getLoanCloseAmount,
  getLoanDetails,
  getManageCollateralExcessAmount,
  getMaxTradeValue,
  getMaxTradeValueToOpenPosition,
  getMaxTradeValueToClosePosition,
  getTradeTokens,
  getUserMarginTradeLoans,
  guessLongShort,
}
